import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useState } from 'react';
import { CriteriaList } from '../criteria-list/CriteriaList';
import { useLazyFetchEligibilitiesQuery } from '../../store/features/eligibility/eligibilitySlice';
import { ModalLoading } from '../modal/ModalLoading';
import { ModalError } from '../modal/ModalError';
import { BaseQueryError } from '../../store/types';
import { ApplyModule } from '../../store/features/apply/types';

interface RequirementModalProps {
  modules: {
    name: string;
    id: number;
  }[];
  buttonLabel?: string;
  module: ApplyModule;
}
const RequirementModal: React.FC<RequirementModalProps> = ({ buttonLabel = 'View Requirements', modules, module }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fetchEligibilities, { isFetching, isError, error, data, isSuccess }] = useLazyFetchEligibilitiesQuery();

  if (modules.length === 0) return null;

  const ids = modules.map((module) => module.id);

  const viewRequirement = (event: React.MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    setIsModalVisible(true);
    if (!isSuccess) {
      fetchEligibilities({ ids, module });
    }
  };

  const closeModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsModalVisible(false);
  };

  const getRequirementById = (id: number): string[][] | undefined => {
    if (data?.eligibilities) {
      const eligibility = data.eligibilities.find((eligibility) => id === eligibility.id);
      if (!eligibility) return undefined;
      return eligibility.messages;
    }
    return undefined;
  };

  return (
    <>
      <Button onClick={viewRequirement} variant="link" sx={{ p: 0, height: 'auto' }}>
        {buttonLabel}
      </Button>
      <Dialog open={isModalVisible} fullWidth maxWidth="md" onClose={closeModal}>
        <Box onClick={(event) => event.stopPropagation()}>
          {isError ? (
            <DialogContent>
              <Box sx={{ mb: 2.5 }}>
                <ModalError handleCancel={closeModal} errorStatus={(error as BaseQueryError)?.status} />
              </Box>
            </DialogContent>
          ) : isFetching ? (
            <DialogContent>
              <ModalLoading loadingTitle="Getting all requirements..." />
            </DialogContent>
          ) : (
            <>
              <DialogTitle>
                {modules.length === 1 ? `Requirements for ${modules[0].name}` : 'View All Requirement Details'}
              </DialogTitle>
              <DialogContent>
                {modules.length === 1 ? (
                  <CriteriaList criteriaArr={getRequirementById(modules[0].id)} />
                ) : (
                  modules.map((module) => (
                    <CriteriaList
                      key={module.id}
                      title={`Requirements for ${module.name}`}
                      criteriaArr={getRequirementById(module.id)}
                    />
                  ))
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeModal} variant="text" size="small">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default RequirementModal;
